import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Lock, CreditCard, ExternalLink } from 'lucide-react';
import { IMAGES, CONTACT_INFO } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Square checkout links. Add more entries as you create payment links in the Square dashboard.
const SQUARE_PAYMENT_LINKS = [
  {
    name: 'Single Home Draw',
    description: 'One patient, one visit — our signature concierge phlebotomy appointment.',
    url: 'https://square.link/u/CO3IDU8r?src=sheet',
  },
  {
    name: 'Family / Multi-Patient Draw',
    description: 'Add additional patients to the same visit — ideal for households and small groups.',
    url: 'https://square.link/u/MmVRN1yy?src=sheet',
  },
  {
    name: 'After-Hours Appointment',
    description: 'Add-on fee for any visit scheduled outside of standard business hours.',
    url: 'https://square.link/u/765zjP44?src=sheet',
  },
  {
    name: 'Specialty Kit',
    description: 'Per specialty kit required for your appointment (e.g., specialized collection tubes or chain-of-custody supplies).',
    url: 'https://square.link/u/GkhCqod9?src=sheet',
  },
  {
    name: 'Specialty Kit — Additional',
    description: 'Each additional specialty kit added to the same visit.',
    url: 'https://square.link/u/rrY4GHYI?src=sheet',
  },
  {
    name: 'Individual BLS Certification',
    description: 'One-on-one Basic Life Support (CPR/AED) certification course — ideal for individual learners.',
    url: 'https://square.link/u/dtdJwO7u?src=sheet',
  },
  {
    name: 'BLS Renewal',
    description: 'Renew your current Basic Life Support (CPR/AED) certification — valid BLS card required.',
    url: 'https://square.link/u/UqfWnOZh?src=sheet',
  },
  {
    name: 'BLS Group Training',
    description: 'On-site Basic Life Support (CPR/AED) training for teams and organizations — we bring the manikins and supplies to you.',
    url: 'https://square.link/u/8fs7EG8s?src=sheet',
  },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: 'Mobile Phlebotomy', date: '', time_window: 'morning', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [blocked, setBlocked] = useState({ dates: [], weekdays: [] });

  useEffect(() => {
    axios.get(`${API}/blocked-dates`).then(({ data }) => {
      // Backward-compatible: server now returns an object, but be safe if array
      if (Array.isArray(data)) setBlocked({ dates: data, weekdays: [] });
      else setBlocked(data || { dates: [], weekdays: [] });
    }).catch((err) => console.error('Failed to fetch blocked dates:', err));
  }, []);

  const isDateBlocked = (v) => {
    if (!v) return null;
    const dHit = blocked.dates?.find((b) => b.date === v);
    if (dHit) return dHit.reason || 'unavailable';
    // JS getDay: Sun=0..Sat=6. Build in local time to avoid TZ drift.
    const [y, m, d] = v.split('-').map(Number);
    if (!y) return null;
    const dow = new Date(y, m - 1, d).getDay();
    const wHit = blocked.weekdays?.find((w) => w.weekday === dow);
    if (wHit) return wHit.reason || 'unavailable';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const reason = isDateBlocked(form.date);
    if (form.date && reason) {
      setError(`That date is unavailable (${reason}). Please choose another.`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', service: 'Mobile Phlebotomy', date: '', time_window: 'morning', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setError(err.response?.data?.detail || 'We could not submit your request. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0f1113] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{ backgroundImage: `url(${IMAGES.contactDesk})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1113] via-[#0f1113]/85 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-28">
          <div className="max-w-xl">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] mb-6 font-medium">CONTACT AURELIA</p>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Let’s Bring Care <br />To You.
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              Schedule your appointment, request a consultation, or ask us anything. We respond quickly — typically within an hour.
            </p>
          </div>
        </div>
      </section>

      {/* Contact grid */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">GET IN TOUCH</p>
            <h2 className="font-serif text-4xl text-[#1c1e20] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              A team of specialists, one call away.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mb-6" />
            <p className="text-[#4a4a4a] leading-relaxed text-[15px] mb-8">
              We are proud to serve individuals, families, and organizations across Atlanta and the surrounding areas.
            </p>

            <ul className="space-y-5">
              {[
                { Icon: Phone, label: 'Phone', value: CONTACT_INFO.phone },
                { Icon: Mail, label: 'Email', value: CONTACT_INFO.email },
                { Icon: MapPin, label: 'Service Area', value: CONTACT_INFO.area },
                { Icon: Clock, label: 'Hours', value: CONTACT_INFO.hours },
              ].map(({ Icon, label, value }) => (
                <li key={label} className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full border border-[#c9a961] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[#c9a961]" strokeWidth={1.4} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.28em] text-[#c9a961] font-semibold mb-1">{label.toUpperCase()}</p>
                    <p className="text-[#1c1e20] text-[15px]">{value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white border border-[#c9a961] p-12 text-center h-full flex flex-col items-center justify-center">
                <img src="/brand/aurelia-logo-v2.png" alt="Aurelia" className="w-40 h-auto object-contain mb-4" />
                <h3 className="font-serif text-3xl text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Thank you.
                </h3>
                <p className="text-[#4a4a4a] max-w-sm">
                  Your request has been received. A member of the Aurelia team will reach out to confirm your appointment shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-black/5 p-8 md:p-10">
                <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">BOOK AN APPOINTMENT</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
                  <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961] bg-white">
                    <option>Mobile Phlebotomy</option>
                    <option>Corporate &amp; Executive Health</option>
                    <option>BLS CPR Classes</option>
                    <option disabled>Legal DNA Testing (Coming Soon)</option>
                    <option disabled>Non-Legal DNA Testing (Coming Soon)</option>
                    <option disabled>DOT Drug &amp; Urine Screening (Coming Soon)</option>
                    <option disabled>Non-DOT Drug &amp; Urine Screening (Coming Soon)</option>
                  </select>
                  <input type="date" value={form.date} onChange={(e) => {
                    const v = e.target.value;
                    const reason = isDateBlocked(v);
                    if (v && reason) setError(`That date is unavailable (${reason}). Please choose another.`);
                    else setError('');
                    setForm({ ...form, date: v });
                  }} className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
                  <select value={form.time_window} onChange={(e) => setForm({ ...form, time_window: e.target.value })} className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961] bg-white">
                    <option value="morning">Morning (8:30 AM – 11:00 AM)</option>
                    <option value="midday">Midday (11:00 AM – 1:30 PM)</option>
                    <option value="afternoon">Afternoon (1:30 PM – 3:30 PM)</option>
                    <option value="evening">Late Afternoon (3:30 PM – 5:00 PM)</option>
                  </select>
                </div>
                <p className="text-[11px] text-[#6b6b6b] mb-4 tracking-wide">Available Sunday through Saturday, 8:30 AM – 5:00 PM.</p>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us more about your needs" rows="5" className="w-full border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961] mb-5" />
                {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold py-4 disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? 'SUBMITTING…' : 'SUBMIT REQUEST'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Secure payment — Square point-of-sale */}
      <section className="bg-[#141618] text-white py-20 md:py-24" data-testid="square-payment-section">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-12">
            <div className="w-14 h-14 rounded-full border border-[#c9a961] flex items-center justify-center mx-auto mb-5">
              <Lock size={20} className="text-[#c9a961]" strokeWidth={1.6} />
            </div>
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-3">SECURE PAYMENT</p>
            <h2
              className="font-serif text-4xl md:text-5xl mb-3 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Pay for your appointment.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-5" />
            <p className="text-white/75 max-w-2xl mx-auto leading-relaxed text-[15px]">
              All payments are processed securely through our Square point-of-sale system.
              Payment is collected before services are rendered. For your convenience, Aurelia also
              accepts <strong className="text-[#c9a961]">Afterpay</strong> (buy-now-pay-later) at
              checkout.
            </p>
          </div>

          <div className={`grid gap-5 ${SQUARE_PAYMENT_LINKS.length > 1 ? 'md:grid-cols-2' : 'max-w-[520px] mx-auto'}`}>
            {SQUARE_PAYMENT_LINKS.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`square-pay-${link.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="group bg-white/[0.04] border border-white/10 hover:border-[#c9a961] transition-colors p-7 flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-[#c9a961]" strokeWidth={1.6} />
                    <span className="text-[10px] tracking-[0.26em] text-[#c9a961] font-semibold">AURELIA</span>
                  </div>
                  <ExternalLink size={14} className="text-white/40 group-hover:text-[#c9a961] transition-colors" />
                </div>
                <h3
                  className="font-serif text-2xl md:text-[26px] text-white mb-2 leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  {link.name}
                </h3>
                <p className="text-white/70 text-[14px] leading-relaxed mb-6">{link.description}</p>
                <span className="mt-auto inline-flex items-center justify-center gap-2 bg-[#c9a961] group-hover:bg-[#b89751] transition-colors text-[#141618] tracking-[0.22em] text-[11px] font-semibold py-3 px-5">
                  PAY SECURELY WITH SQUARE
                </span>
              </a>
            ))}
          </div>

          <p className="text-center text-[12px] text-white/50 mt-8 italic max-w-xl mx-auto">
            Clicking any payment card opens Square&rsquo;s secure checkout in a new tab. Aurelia never
            handles or stores your card details — all card data stays with Square.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
